import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react'
import { Icon, type IconName } from '../Icon'

/**
 * The committing button. Dimensional — see the .b3d / .b3ds block in
 * styles/application.css for the fill, the rim and the press.
 *
 * Geometry is the design system's Button unchanged (rounded-lg, px-4 py-2,
 * 14px medium): the prototype uses `Wingspace.Button` for exactly these roles
 * and the redesign changes only how they are lit. What it doesn't inherit is
 * the flat accent fill — the accent lives in the rim and the label now, so
 * nothing in the product is set in black text on brass.
 *
 * The classes go on rather than being left to the `button.bg-accent` selectors
 * that upgrade the legacy component, because this one also renders as an <a>
 * when given an href, and an anchor is not a button.
 *
 * **Two variants became four, and it grew `size` and `icon`** (Alex,
 * 2026-09-01), so the old components/Button.tsx could go. Each addition is
 * something the old one carried and the screens still using it need:
 *
 *   danger / dangerOutline  the destructive pair. Not invented here — the
 *       redesign was already hand-rolling this treatment inline in the
 *       delete-account sheet, and it is the same rim/fill/ink the failure
 *       states and the danger StatusChip use. Now tokens (--w-danger-*), which
 *       also fixed it in light mode, where the hard-coded ink measured 1.77:1.
 *   size sm  a control in a table row or a repeated form row that should be a
 *       real button without dominating the row. The alternative was styling
 *       those as text, which stopped reading as pressable.
 *   icon  a leading glyph, by name rather than node, so "add" looks the same
 *       everywhere. For actions that add, remove, navigate or export — not for
 *       plain submits ("Save changes", "Sign in") or Cancel, where a mark on
 *       every control is what turns a set of icons back into noise.
 *
 * Anything smaller and quieter than the sm here is an `Action`, which is the
 * chip that lives in a card header or a sheet footer.
 */

type Variant = 'primary' | 'secondary' | 'danger' | 'dangerOutline'

const VARIANTS: Record<Variant, string> = {
  primary: 'b3d bg-accent text-amber-ink',
  // .b3dq, the same wash and rim the quiet Action chip wears (Alex,
  // 2026-09-01). It used to be .b3ds over an opaque bg-pane with a real
  // border, which made the one control on a card that didn't let the ground
  // through — a slab, next to a card that is itself a translucent pane.
  secondary: 'b3dq text-text',
  // The committing destructive control — the "yes, delete it" inside a sheet,
  // where it is the only destructive thing on screen. Dimensional like the
  // primary, in the failure treatment rather than the accent one.
  danger: 'b3ds w-danger',
  // The control that *starts* something destructive, sitting among ordinary
  // ones. Shaped like a secondary so it doesn't shout, failure ink so there is
  // no mistaking what it does — so it follows secondary onto .b3dq, and
  // .w-danger-ink brings the rim on hover now that there is no border to tint.
  dangerOutline: 'b3dq w-danger-ink',
}

type Size = 'sm' | 'md'

const SIZES: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
}

// The glyph tracks the label's size rather than sitting at one fixed 16px: an
// icon drawn the same size as 12px text overpowers it, and the pair stops
// reading as one word with a mark in front of it.
const ICON_SIZES: Record<Size, string> = { sm: 'h-3.5 w-3.5', md: 'h-4 w-4' }

// Gap only when there is something to space.
const GAPS: Record<Size, string> = { sm: 'gap-1.5', md: 'gap-2' }

interface Shared {
  variant?: Variant
  size?: Size
  icon?: IconName
  children: ReactNode
  className?: string
}

type ButtonProps =
  | (Shared & ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined })
  | (Shared & AnchorHTMLAttributes<HTMLAnchorElement> & { href: string })

const BASE =
  'inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-lg font-body font-medium disabled:cursor-default disabled:opacity-50'

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  className = '',
  children,
  href,
  ...props
}: ButtonProps) {
  const classes = `${BASE} ${icon ? GAPS[size] : ''} ${SIZES[size]} ${VARIANTS[variant]} ${className}`
  const content = (
    <>
      {icon && <Icon name={icon} className={ICON_SIZES[size]} />}
      {children}
    </>
  )

  if (href) {
    return (
      <a href={href} className={classes} {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {content}
      </a>
    )
  }

  return (
    <button type="button" className={classes} {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}>
      {content}
    </button>
  )
}
